import type { CalibrationPoint } from '../../../types/device';

export interface ScannedHoldOutline {
  key: string;
  outline: CalibrationPoint[];
  /** The role's colour on a hold the problem uses; null on one it does not. */
  color: string | null;
  dimmed?: boolean;
  /** Drawn as the selected hold, a touch heavier. */
  selected?: boolean;
}

/** An untaken hold: there to be pressed, quiet enough to read the wall through. */
const EMPTY_STROKE = 'rgba(255,255,255,0.45)';
const DIMMED_OPACITY = 0.3;

/**
 * **The holds of a scanned wall, drawn as they are** (docs/SPRAY_WALL_SCAN_PLAN.md):
 * the outline the scan traced round each one, coloured by the role it plays in
 * the problem, instead of a circle guessed at its size.
 *
 * One SVG over the picture, in the picture's own fractions (`viewBox 0 0 1 1`,
 * stretched with it), so it scales and pans with the photo as one thing. The
 * line keeps its width on screen (`non-scaling-stroke`). It takes no pointer:
 * the round target laid over each hold is what is pressed, and what carries
 * the circuit's numbers and the hand marks.
 */
export function ScannedHoldOutlines({ holds }: { holds: ScannedHoldOutline[] }) {
  if (holds.length === 0) return null;

  return (
    <svg
      className="pointer-events-none absolute inset-0 size-full"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      aria-hidden
      data-testid="scanned-hold-outlines"
    >
      {holds.map(({ key, outline, color, dimmed, selected }) => (
        <polygon
          key={key}
          data-hold={key}
          points={outline.map((point) => `${point.x},${point.y}`).join(' ')}
          fill={color ? `${color}55` : 'none'}
          stroke={color ?? EMPTY_STROKE}
          strokeWidth={selected ? 4 : color ? 2.5 : 1}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity={dimmed ? DIMMED_OPACITY : 1}
        />
      ))}
    </svg>
  );
}
